import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { adminService } from '../../services/admin';
import {
  InstitutionDto,
  getInstitutionDisplayName,
  InstitutionMembershipCategory,
  AdministrativeStatus,
} from '../../types/institution';
import { PagedResponse } from '../../types/member';
import { tryNormalizeEnumValue } from '../../utils/enumNormalize';
import { Container, Stack } from '../../design-system/components/Layout';
import { Card } from '../../design-system/components/Card';
import { Button } from '../../design-system/components/Button';
import { Heading } from '../../design-system/components/Typography';
import { PageHeader } from '../../design-system/patterns/PageHeader';
import SearchBar from '../../components/common/SearchBar';
import { DataTable, Column } from '../../components/common/DataTable';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { animations } from '../../design-system/tokens/animations';
import { shadows } from '../../design-system/tokens/shadows';

const AdminInstitutionsContainer = styled(Container)`
  max-width: 1400px;
`;
const FiltersSection = styled(Card)`
  padding: ${spacing[6]};
  margin-bottom: ${spacing[6]};
`;
const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing[4]};
  align-items: end;
`;
const FilterLabel = styled.label`
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
`;
const SelectStyled = styled.select`
  padding: ${spacing[3]} ${spacing[4]};
  border: ${borders.width.base} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.md};
  font-size: ${typography.fontSize.sm};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  transition: ${animations.transition(['border-color', 'box-shadow'], animations.duration.normal, animations.easing.easeOut)};
  font-family: ${typography.fontFamily.primary};
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => shadows.focus(props.theme.colors.primary)};
  }
`;
const NameCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
`;
const Initials = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${borders.radius.full};
  background: ${props => props.theme.colors.gradientSecondary};
  color: ${props => props.theme.colors.text};
  font-weight: ${typography.fontWeight.semibold};
  font-size: ${typography.fontSize.xs};
  font-family: ${typography.fontFamily.display};
  flex-shrink: 0;
`;
const PAGE_SIZE = 25;

const AdminInstitutionsList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [institutions, setInstitutions] = useState<InstitutionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const loadInstitutions = async () => {
      setLoading(true);
      try {
        const response: PagedResponse<InstitutionDto> = await adminService.getInstitutions({
          page,
          pageSize: PAGE_SIZE,
          search: debouncedSearch || undefined,
          membershipCategory: categoryFilter ? parseInt(categoryFilter, 10) : undefined,
        });
        setInstitutions(response.items);
        setTotal(response.total);
      } catch (error) {
        console.error('Error loading institutions:', error);
      } finally {
        setLoading(false);
      }
    };
    loadInstitutions();
  }, [page, debouncedSearch, categoryFilter]);

  const handleInstitutionClick = (institution: InstitutionDto) => {
    navigate(`/admin/institutions/${institution.id}`);
  };

  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setCategoryFilter('');
    setStatusFilter('');
    setPage(1);
  };

  const getInitials = (name?: string) => {
    if (!name || !name.trim()) return '—';
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
      return `${words[0].charAt(0)}${words[words.length - 1].charAt(0)}`.toUpperCase();
    }
    return words[0].substring(0, 2).toUpperCase();
  };

  const getMembershipCategoryLabel = (
    category?: InstitutionMembershipCategory | string | number | null
  ): string => {
    const labels: Record<InstitutionMembershipCategory, string> = {
      [InstitutionMembershipCategory.ActiveI]: t('register.budgetLessThan30k'),
      [InstitutionMembershipCategory.ActiveII]: t('register.budget30kTo100k'),
      [InstitutionMembershipCategory.ActiveIII]: t('register.budget100kTo1M'),
      [InstitutionMembershipCategory.ActiveIV]: t('register.budget1MTo5M'),
      [InstitutionMembershipCategory.ActiveV]: t('register.budget5MTo10M'),
      [InstitutionMembershipCategory.ActiveVI]: t('register.budgetMoreThan10M'),
      [InstitutionMembershipCategory.Benefactor]: t('register.budgetBenefactor'),
    };
    const normalized = tryNormalizeEnumValue(InstitutionMembershipCategory, category);
    if (normalized == null) return t('common.unknown', { defaultValue: 'Unknown' });
    return labels[normalized] || t('common.unknown', { defaultValue: 'Unknown' });
  };

  const getAdministrativeStatusLabel = (
    status?: AdministrativeStatus | string | number | null
  ): string => {
    if (status == null || status === '') return '—';
    const labels: Record<AdministrativeStatus, string> = {
      [AdministrativeStatus.Public]: t('institution.administrativeStatus.public'),
      [AdministrativeStatus.Private]: t('institution.administrativeStatus.private'),
      [AdministrativeStatus.Mixed]: t('institution.administrativeStatus.mixed'),
    };
    const normalized = tryNormalizeEnumValue(AdministrativeStatus, status);
    if (normalized == null) return t('common.unknown', { defaultValue: 'Unknown' });
    return labels[normalized] || t('common.unknown', { defaultValue: 'Unknown' });
  };

  const filteredInstitutions = institutions.filter((institution) => {
    if (statusFilter && institution.administrativeStatus?.toString() !== statusFilter) {
      return false;
    }
    return true;
  });

  const hasActiveFilters = !!search || !!categoryFilter || !!statusFilter;

  const institutionColumns: Column<InstitutionDto>[] = [
    {
      key: 'name',
      header: t('admin.name'),
      render: (inst) => (
        <NameCell>
          <Initials>{getInitials(getInstitutionDisplayName(inst))}</Initials>
          <span>{getInstitutionDisplayName(inst)}</span>
        </NameCell>
      ),
    },
    {
      key: 'contact',
      header: t('admin.contact'),
      render: (inst) => inst.institutionEmail || inst.contactPersonEmail || '—',
    },
    {
      key: 'category',
      header: t('admin.category'),
      render: (inst) => getMembershipCategoryLabel(inst.membershipCategory),
    },
    {
      key: 'status',
      header: t('admin.administrativeStatus'),
      render: (inst) => getAdministrativeStatusLabel(inst.administrativeStatus),
    },
    {
      key: 'city',
      header: t('admin.city'),
      render: (inst) => inst.primaryAddress?.cityEn ?? inst.primaryAddress?.cityGr ?? '—',
    },
    {
      key: 'year',
      header: t('admin.registrationYear'),
      render: (inst) => inst.registrationYear ?? '—',
    },
    {
      key: 'memberCards',
      header: t('admin.memberCards'),
      render: (inst) => String(inst.numberOfMemberCards),
    },
    {
      key: 'actions',
      header: t('admin.actions'),
      actions: (inst) => (
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleInstitutionClick(inst);
          }}
        >
          {t('admin.view')}
        </Button>
      ),
    },
  ];

  return (
    <AdminInstitutionsContainer>
      <Stack direction="column" spacing="xl">
        <PageHeader
          title={t('admin.institutions')}
          subtitle={
            !loading
              ? total === 0
                ? t('admin.showingResults', { start: 0, end: 0, total: 0 })
                : t('admin.showingResults', {
                    start: (page - 1) * PAGE_SIZE + 1,
                    end: Math.min(page * PAGE_SIZE, total),
                    total,
                  })
              : undefined
          }
          pattern={true}
          actions={
            <Button variant="primary" onClick={() => navigate('/register-institution?mode=admin')}>
              {t('admin.addInstitutionMember')}
            </Button>
          }
        />

        <FiltersSection variant="elevated" accentBorder="top">
          <Stack direction="column" spacing="md">
            <Heading variant="h3" color="text">
              {t('admin.filters')}
            </Heading>
            <FiltersGrid>
              <Stack direction="column" spacing="sm">
                <FilterLabel>{t('common.search')}</FilterLabel>
                <SearchBar
                  value={search}
                  onChange={setSearch}
                  placeholder={t('admin.searchPlaceholder')}
                  fullWidth
                />
              </Stack>
              <Stack direction="column" spacing="sm">
                <FilterLabel htmlFor="admin-institutions-category">{t('admin.category')}</FilterLabel>
                <SelectStyled
                  id="admin-institutions-category"
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t('admin.all')}</option>
                  <option value={InstitutionMembershipCategory.ActiveI.toString()}>{getMembershipCategoryLabel(InstitutionMembershipCategory.ActiveI)}</option>
                  <option value={InstitutionMembershipCategory.ActiveII.toString()}>{getMembershipCategoryLabel(InstitutionMembershipCategory.ActiveII)}</option>
                  <option value={InstitutionMembershipCategory.ActiveIII.toString()}>{getMembershipCategoryLabel(InstitutionMembershipCategory.ActiveIII)}</option>
                  <option value={InstitutionMembershipCategory.ActiveIV.toString()}>{getMembershipCategoryLabel(InstitutionMembershipCategory.ActiveIV)}</option>
                  <option value={InstitutionMembershipCategory.ActiveV.toString()}>{getMembershipCategoryLabel(InstitutionMembershipCategory.ActiveV)}</option>
                  <option value={InstitutionMembershipCategory.ActiveVI.toString()}>{getMembershipCategoryLabel(InstitutionMembershipCategory.ActiveVI)}</option>
                  <option value={InstitutionMembershipCategory.Benefactor.toString()}>{getMembershipCategoryLabel(InstitutionMembershipCategory.Benefactor)}</option>
                </SelectStyled>
              </Stack>
              <Stack direction="column" spacing="sm">
                <FilterLabel htmlFor="admin-institutions-status">{t('admin.administrativeStatus')}</FilterLabel>
                <SelectStyled
                  id="admin-institutions-status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t('admin.all')}</option>
                  <option value={AdministrativeStatus.Public.toString()}>
                    {t('institution.administrativeStatus.public')}
                  </option>
                  <option value={AdministrativeStatus.Private.toString()}>
                    {t('institution.administrativeStatus.private')}
                  </option>
                  <option value={AdministrativeStatus.Mixed.toString()}>
                    {t('institution.administrativeStatus.mixed')}
                  </option>
                </SelectStyled>
              </Stack>
              {hasActiveFilters && (
                <Stack direction="row" justify="end">
                  <Button variant="outline" onClick={clearFilters}>
                    {t('admin.clearFilters')}
                  </Button>
                </Stack>
              )}
            </FiltersGrid>
          </Stack>
        </FiltersSection>

        <DataTable<InstitutionDto>
          data={filteredInstitutions}
          columns={institutionColumns}
          loading={loading}
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
          onRowClick={handleInstitutionClick}
          emptyMessage={t('admin.noInstitutions')}
        />
      </Stack>
    </AdminInstitutionsContainer>
  );
};
export default AdminInstitutionsList;
